/**
 * Helper function to get the present state based on the currentIndex in history
 * @param {State} state
 * @returns {WheelState}
 */
export const getPresentState = (state) =>
  state.segmentBuilder.history[state.segmentBuilder.currentIndex];

/**
 * Gets the type.
 * @param {State} state
 * @returns {number|null}
 */
export const getWheelType = (state) => getPresentState(state).type;

export const getHasUnsavedChanges = (state) =>
  state.segmentBuilder.hasUnsavedChanges;

/**
 * Gets the business box.
 * @param {State} state
 * @returns {BusinessBox}
 */
export const getBusinessBox = (state) => getPresentState(state).business_box;

/**
 * Gets the is_primary flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsPrimary = (state) => getPresentState(state).is_primary;

/**
 * Gets the is_public flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsPublic = (state) => getPresentState(state).is_public;

/**
 * Gets the is_locked flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsLocked = (state) => getPresentState(state).is_locked;

/**
 * Gets the show_business_box flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getShowBusinessBox = (state) =>
  getPresentState(state).show_business_box;

export const getWheelBusinessBox = (state) =>
  getPresentState(state).business_box;

/**
 * Gets the wheel name.
 * @param {State} state
 * @returns {string}
 */
export const getWheelName = (state) => getPresentState(state).wheel_name;

/**
 * Gets the description.
 * @param {State} state
 * @returns {string}
 */
export const getDescription = (state) => getPresentState(state).description;

/**
 * Gets the sub title.
 * @param {State} state
 * @returns {string}
 */
export const getSubTitle = (state) => getPresentState(state).sub_title;

/**
 * Gets the curated wheel categories (ISP-9999, stgport-education-category-discover-wheel-tags).
 * @param {State} state
 * @returns {string[]}
 */
export const getCategories = (state) =>
  getPresentState(state).categories || [];

/**
 * Gets the custom wheel tags (ISP-9999, stgport-education-category-discover-wheel-tags).
 * @param {State} state
 * @returns {Array<{name: string}>}
 */
export const getWheelTags = (state) =>
  getPresentState(state).wheel_tags || [];

/**
 * Gets the wheel segments.
 * @param {State} state
 * @returns {WheelSegment[]}
 */
export const getWheelSegments = (state) =>
  getPresentState(state).wheel_segments;
export const getWheelAutoResizing = (state) =>
  getPresentState(state).automatic_resizing;
export const getWheelContentHeights = (state) =>
  getPresentState(state).content_heights;
export const getWheelIsDraftValue = (state) => getPresentState(state).is_draft;
export const getWheelIsLiveValue = (state) => getPresentState(state).is_live;
export const getWheelIsClonableValue = (state) =>
  getPresentState(state).is_clonable;

// minors-13plus B.14: the creator's maturity self-label ("general" | "mature").
// Absent on wheels saved before the field shipped, so callers normalise through
// isMatureRating() rather than trusting a bare truthiness check.
export const getWheelContentRating = (state) =>
  getPresentState(state).content_rating;

export const getWheelId = (state) => getPresentState(state).id;

/**
 * Gets the background.
 * @param {State} state
 * @returns {Background}
 */
export const getBackground = (state) => getPresentState(state).background;

/**
 * Gets the image links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getImageLinks = (state) => getPresentState(state).image_links;

/**
 * Gets the video links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getVideoLinks = (state) => getPresentState(state).video_links;

/**
 * Gets the document links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getDocumentLinks = (state) =>
  getPresentState(state).document_links;

/**
 * Gets the audio links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getAudioLinks = (state) => getPresentState(state).audio_links;

/**
 * Gets the starred links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getStarredLinks = (state) => getPresentState(state).starred_links;

/**
 * Gets the wheel image.
 * @param {State} state
 * @returns {string}
 */
export const getWheelImage = (state) =>
  getPresentState(state)?.wheel_center_media;

export const getCloningStatus = (state) => getPresentState(state).is_cloning;

export const getSegmentIcon = (state) => getPresentState(state).segment_icon;

export const getIsHidden = (state) => getPresentState(state).is_hidden;

export const getVisibility = (state) => getPresentState(state).visibility;

export const getIsWheelSavingLoader = (state) =>
  getPresentState(state).isSavingWheel;

export const getWheelTitle = (state) => getPresentState(state).wheel_name;

export const getWheelAuthorId = (state) => getPresentState(state).user;

export const getSelectedMenu = (state) => getPresentState(state).selectedMenu;

export const getDesignTabTarget = (state) =>
  getPresentState(state).designTabTarget;

export const getSidePaneVisible = (state) =>
  getPresentState(state).isSidePaneVisible;

export const getToolBarVisible = (state) =>
  getPresentState(state).isToolBarVisible;

export const getSegmentSetting = (state) =>
  getPresentState(state).isSegmentSettingVisible;

export const getRotateBoxVisibility = (state) =>
  getPresentState(state).is_rotate_box_open;

export const getSelectedSegment = (state) => {
  return getPresentState(state)?.wheel_segments?.find(
    (segment) => segment.selected === true
  );
};

export const getFroalaFocus = (state) => state.segmentBuilder.froala_focus;

export const getLockEmails = (state) =>
  getPresentState(state).lock_type.permission_lock.emails;
export const getLockAccessCodes = (state) =>
  getPresentState(state).lock_type.access_code_lock.access_codes;

export const getLockEmailPermission = (state) =>
  getPresentState(state).lock_type.permission_lock.is_active;

export const getLockAccessCodePermission = (state) =>
  getPresentState(state).lock_type.access_code_lock.is_active;

export const getGlobalSettings = (state) =>
  getPresentState(state).globalSettings;

export const getisGlobalSettingsApplied = (state) =>
  getPresentState(state).isGlobalSettingsApplied;

export const getisGlobalCornerRoundingApplied = (state) =>
  getPresentState(state).isGlobalCornerRoundingApplied;

export const getisGlobalBorderThicknessApplied = (state) =>
  getPresentState(state).isGlobalBorderThicknessApplied;

export const getisGlobalHeadingPropsApplied = (state) =>
  getPresentState(state).isGlobalHeadingPropsApplied;

export const getisGlobalParapgraphPropsApplied = (state) =>
  getPresentState(state).isGlobalParagraphPropsApplied;

export const getisGlobalLineHeightApplied = (state) =>
  getPresentState(state).isGlobalLineHeightApplied;

export const getisGlobalMarginApplied = (state) =>
  getPresentState(state).isGlobalMarginApplied;

export const getSegmentColorPalette = (state) => {
  const selectedArc = getPresentState(state)?.wheel_segments?.find(
    (segment) => segment.selected === true
  );

  const segment = getPresentState(state).wheel_segments.find(
    (seg) => seg.id === selectedArc?.id
  );
  return segment
    ? segment.settings.palette_data || segment.settings.paletteData
    : null;
};

export const getWheelColorPalette = (state) =>
  getPresentState(state).palette_data ||
  getPresentState(state).paletteData ||
  null;

export const getSelectedSegmentCharCount = (state) =>
  getPresentState(state).selected_segment_char_count || 0;

// ---------------------------------------------------------------------------
// STAGING PORT GAP (2026-09-18). The components below were ported to this
// branch WITHOUT these selectors, so each named import resolved to `undefined`.
// `useSelector(undefined)` throws inside react-redux, which is why EVERY
// /builder/<id> rendered the "Something went wrong" error boundary on
// web-stg.iyuga.net while web-dev.iyuga.net was fine.
// Restored verbatim from origin/dev. Importers:
//   getResizeSegmentMode  - pages/SegmentBuilder/index.jsx,
//                           components/SegmentBuilder/resize/ResizeSegmentModeToggle.jsx
//   getCanUndo/getCanRedo - pages/SegmentBuilder/index.jsx,
//                           components/SegmentBuilder/Shared/UndoRedoControls.jsx
//   getUndoRedoRevision   - components/SegmentView/index.jsx
// Guarded by tools/import-export-gate/check-redux-named-imports.mjs.
// ---------------------------------------------------------------------------

/** True when there is an earlier (undo) state to move back to. */
export const getCanUndo = (state) => state.segmentBuilder.currentIndex > 0;

/** True when there is a later (redo) state to move forward to. */
export const getCanRedo = (state) =>
  state.segmentBuilder.currentIndex <
  state.segmentBuilder.history.length - 1;

/**
 * Bumped on every undo/redo so consumers that cache derived render state can
 * tell a history jump apart from an ordinary edit.
 */
export const getUndoRedoRevision = (state) =>
  state.segmentBuilder.undoRedoRevision || 0;

/**
 * V1-RESIZE: whether the per-Segment Settings pane is showing the pure
 * per-Segment resize view. Top-level ephemeral UI field, outside undo history.
 */
export const getSegmentResizeView = (state) =>
  !!state.segmentBuilder.segmentResizeView;

/**
 * W-A0: whether the user has explicitly entered "Resize Segment" mode via the
 * side-pane toggle. The wedge drag-resize overlay mounts its grips ONLY while
 * this is true, so the drag stays inert unless deliberately turned on.
 */
export const getResizeSegmentMode = (state) =>
  state.segmentBuilder.resizeSegmentMode || false;
